/**
 * ReplaceInformantFlow
 * ====================
 *
 * Two-step flow for replacing an existing informant with a new person.
 *
 * Step 0 — Enter new informant details (name, email, relationship)
 * Step 1 — Review & confirm (shows before/after recap, progress warnings)
 * Done   — Success confirmation screen
 *
 * The component owns all form state internally, so unmounting it (e.g.
 * via a `key` change on the parent) resets the form cleanly. This also
 * isolates it from Firestore onSnapshot-triggered re-renders in the
 * parent, preventing the success screen from being torn away.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import StepIndicator from './StepIndicator';
import WarningPanel from './WarningPanel';
import BeforeAfterRecap from './BeforeAfterRecap';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';
import InformantFormInput from './InformantFormInput';
import {
  validateNewInformant,
  validateEmailNotDuplicate,
} from '../../utils/informantValidation';

/**
 * @param {Object}   props
 * @param {Object}   props.informant         – The informant being replaced
 * @param {Array}    props.allInformants      – All current informants (for duplicate email check)
 * @param {Function} props.onReplace          – (oldInformantId, { newInformant }) => Promise
 * @param {Function} props.onBack             – Navigate back to the detail screen
 * @param {boolean}  props.actionLoading      – Whether the backend call is in progress
 * @param {string}   props.actionError        – Error message from the backend
 * @param {Function} props.clearActionState   – Reset actionError/actionSuccess
 * @param {Function} props.onFinish           – Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing        – Show spinner on the "Finish" button
 * @param {Object}   props.terminology        – { singular, lowerSingular }
 * @param {Function} props.sanitizeNameInput  – Name input sanitizer
 * @param {Function} props.sanitizeEmailInput – Email input sanitizer
 */
export default function ReplaceInformantFlow({
  informant,
  allInformants = [],
  onReplace,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
  sanitizeNameInput,
  sanitizeEmailInput,
  callerType = 'provider',
  isAutismPathway = false,
}) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', relationship: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const hadProgress = informant?.hasStarted;

  const handleFormChange = useCallback((field, value) => {
    let sanitized = value;
    if (field === 'firstName' || field === 'lastName' || field === 'relationship') {
      sanitized = sanitizeNameInput?.(value) ?? value;
    } else if (field === 'email') {
      sanitized = sanitizeEmailInput?.(value) ?? value;
    }
    setFormData(prev => ({ ...prev, [field]: sanitized }));
    if (formErrors[field]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  }, [formErrors, sanitizeNameInput, sanitizeEmailInput]);

  const handleProceedToConfirm = useCallback(() => {
    const errors = validateNewInformant(formData, allInformants);
    if (!errors.email) {
      const dupErr = validateEmailNotDuplicate(formData.email, allInformants);
      if (dupErr) errors.email = dupErr;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    clearActionState?.();
    setStep(1);
  }, [formData, allInformants, clearActionState]);

  const handleConfirm = useCallback(async () => {
    try {
      await onReplace(informant.id, {
        newInformant: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          relationship: formData.relationship.trim(),
        },
      });
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onReplace, informant, formData]);

  // ── Success screen ──
  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline={`${terminology.singular} replaced successfully!`}
        detail={`${formData.firstName.trim()} ${formData.lastName.trim()} has been assigned the questionnaires. A welcome email has been sent.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  // ── Step 1: Confirm ──
  if (step === 1) {
    return (
      <div className="space-y-4">
        <StepIndicator steps={['Details', 'Confirm']} currentStep={1} />

        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Confirm Replacing {terminology.singular}
          </h3>
        </div>

        <WarningPanel variant={hadProgress ? 'warning' : 'info'}>
          {hadProgress ? (
            <>
              <p className="font-semibold mb-1">Important: This cannot be undone.</p>
              <p>This will archive the current {terminology.lowerSingular}'s questionnaire progress and remove their access.</p>
            </>
          ) : (
            <p>This will remove {informant.firstName}'s access and assign the new {terminology.lowerSingular} to {isAutismPathway ? 'the developmental history questionnaires' : 'the same questionnaires'}.</p>
          )}
        </WarningPanel>

        <BeforeAfterRecap
          before={[{
            id: informant.id,
            name: `${informant.firstName} ${informant.lastName}`,
            lifeStages: isAutismPathway ? ['Developmental history'] : (informant.lifeStages || []),
          }]}
          after={[{
            id: 'new',
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            lifeStages: isAutismPathway ? ['Developmental history'] : (informant.lifeStages || []),
            isNew: true,
          }]}
          operationLabel={`Replace ${terminology.singular}`}
        />

        <WarningPanel variant="info">
          A welcome email will be sent to <span className="font-semibold break-all">{formData.email.trim()}</span>.
        </WarningPanel>

        {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => { clearActionState?.(); setStep(0); }}
            disabled={actionLoading}
            className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={actionLoading}
            className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
              actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {actionLoading ? (
              <span className="inline-flex items-center gap-2">
                <ButtonSpinner />
                Replacing…
              </span>
            ) : `Confirm Replacing ${terminology.singular}`}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 0: Details form ──
  return (
    <div className="space-y-4">
      <StepIndicator steps={['Details', 'Confirm']} currentStep={0} />

      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Replace {terminology.singular}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Enter the details of the new {terminology.lowerSingular} who will replace{' '}
          <span className="font-medium">{informant.firstName} {informant.lastName}</span>.
        </p>
      </div>

      <WarningPanel variant={hadProgress ? 'warning' : 'info'}>
        {hadProgress ? (
          <>
            <p className="font-semibold mb-1">This {terminology.lowerSingular} has started their questionnaires.</p>
            <p>
              Replacing them will archive their progress and remove their access.
              The new {terminology.lowerSingular} will start fresh.
            </p>
          </>
        ) : (
          <p>
            This will remove {informant.firstName}'s access and assign the
            new {terminology.lowerSingular} to the same questionnaires.
          </p>
        )}
      </WarningPanel>

      <div className="space-y-3">
        <InformantFormInput
          label="First name"
          value={formData.firstName}
          onChange={(e) => handleFormChange('firstName', e.target.value)}
          error={formErrors.firstName}
          required
          autoComplete="given-name"
        />
        <InformantFormInput
          label="Last name"
          value={formData.lastName}
          onChange={(e) => handleFormChange('lastName', e.target.value)}
          error={formErrors.lastName}
          required
          autoComplete="family-name"
        />
        <InformantFormInput
          label="Email address"
          type="email"
          value={formData.email}
          onChange={(e) => handleFormChange('email', e.target.value)}
          error={formErrors.email}
          required
          autoComplete="email"
        />
        <InformantFormInput
          label={callerType === 'patient' ? 'Relationship to you' : 'Relationship to patient'}
          value={formData.relationship}
          onChange={(e) => handleFormChange('relationship', e.target.value)}
          error={formErrors.relationship}
          required
          placeholder="e.g. Parent, Spouse, Sibling"
        />
      </div>

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleProceedToConfirm}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Review Details →
        </button>
      </div>
    </div>
  );
}
